/* eslint-disable max-lines */
import { FC, useEffect, useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Sector } from "recharts";
import { cx as classNames } from "../../../utils/classnames";
import { eur } from "../../../utils/eur";
import InfoTip from "../../ui/InfoTip";
import SpinnerLoader from "../../ui/SpinnerLoader";

export interface IDonutSlice {
  name: string;
  value: number;
  color: string;
}

const EMPTY_RING: IDonutSlice[] = [{ name: "", value: 1, color: "#EAEAEA" }];

// Same ring and centre read-out as OfficeAndOnlineSalesAnalytics.
const renderActiveShape = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, value } =
    props;

  return (
    <g>
      <text x={cx} y={cy - 16} dy={10} textAnchor="middle" fill={fill} className="text-sm font-medium">
        {payload.name}
      </text>
      <text x={cx} y={cy + 12} dy={10} textAnchor="middle" className="text-2xl font-medium text-black">
        {eur(value)}
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        cornerRadius={100}
        forceCornerRadius={false}
        fill={fill}
      />
    </g>
  );
};

const CreditDonut: FC<{
  title: string;
  info: string;
  data: IDonutSlice[];
  loading?: boolean;
  className?: string;
}> = ({ title, info, data, loading, className }) => {
  const slices = data.filter((d) => d.value > 0);
  const total = slices.reduce((sum, d) => sum + d.value, 0);
  const largest = slices.reduce(
    (best, d, i) => (d.value > slices[best].value ? i : best),
    0
  );

  const [activeIndex, setActiveIndex] = useState(largest);
  useEffect(() => setActiveIndex(largest), [largest, total]);

  return (
    <div
      className={`bg-white border border-gray-200 rounded-lg px-6 py-4 flex flex-col ${className ?? ""}`}
      onMouseLeave={() => setActiveIndex(largest)}
    >
      <div className="flex items-center gap-1">
        <p className="text-xs uppercase leading-6 font-medium text-neutral">
          {title}
        </p>
        <InfoTip text={info} className="w-3.5 h-3.5" />
      </div>
      {loading ? (
        <div className="h-6 w-20 bg-gray-200 rounded-md animate-pulse" />
      ) : (
        <p className="text-sm leading-6 font-bold">{eur(total)}</p>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center gap-x-8 gap-y-2 mt-2">
        <div className="h-[180px] w-[180px] shrink-0 mx-auto sm:mx-0 relative">
          {loading ? (
            <SpinnerLoader className="inset-0 h-max m-auto absolute" />
          ) : null}
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                activeIndex={total > 0 ? activeIndex : undefined}
                activeShape={renderActiveShape}
                data={total > 0 ? slices : EMPTY_RING}
                innerRadius={80}
                outerRadius={88}
                paddingAngle={slices.length > 1 ? 4 : 0}
                cornerRadius="100%"
                dataKey="value"
                isAnimationActive={false}
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onClick={(_, index) => setActiveIndex(index)}
              >
                {(total > 0 ? slices : EMPTY_RING).map((d, index) => (
                  <Cell key={`cell-${index}`} fill={d.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className="flex flex-col gap-1.5 flex-1 min-w-0">
          {data.map((d) => (
            <li
              key={d.name}
              className={classNames([
                "flex items-center justify-between gap-3 text-sm rounded px-1 -mx-1",
                slices.indexOf(d) >= 0 && "cursor-default hover:bg-gray-100",
                total > 0 && slices.indexOf(d) === activeIndex && "bg-gray-100"
              ])}
              onMouseEnter={() => slices.indexOf(d) >= 0 && setActiveIndex(slices.indexOf(d))}
            >
              <span className="flex items-center gap-2 font-medium min-w-0">
                <span
                  className="h-1.5 w-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: d.color }}
                />
                <span className="truncate">{d.name}</span>
              </span>
              <span className="font-medium whitespace-nowrap">{eur(d.value)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default CreditDonut;
